package com.schoolconnect.app.feature.homework

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.Image
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogDateField
import com.schoolconnect.app.designsystem.AppDialogDefaultColors
import com.schoolconnect.app.designsystem.AppDialogFieldColumn
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogFormGrid
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSelectField
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppDialogValidationSummary
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppOutlinedButton
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.appDateLabelAfterToday
import com.schoolconnect.app.designsystem.currentAppDateLabel
import com.schoolconnect.app.feature.common.compareBySmartText
import com.schoolconnect.app.feature.common.ShellProfileStore
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.util.decodeImageBitmap
import com.schoolconnect.app.util.resizeImage
import io.github.vinceglb.filekit.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.core.PickerMode
import io.github.vinceglb.filekit.core.PickerType
import io.github.vinceglb.filekit.core.PlatformFile
import kotlinx.coroutines.launch
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.cloud_upload
import ischoolconnect.sharedui.generated.resources.close_x
import ischoolconnect.sharedui.generated.resources.document
import ischoolconnect.sharedui.generated.resources.search
import ischoolconnect.sharedui.generated.resources.user
import org.jetbrains.compose.resources.painterResource

private data class HomeworkAttachmentUi(
    val file: PlatformFile?,
    val name: String,
    val type: String,
    val sizeLabel: String,
    val fileId: String? = null,
    val previewBytes: ByteArray? = null
)

@Composable
internal fun AddHomeworkDialog(
    visible: Boolean,
    onDismiss: () -> Unit,
    initialHomework: HomeworkRowUi? = null,
    classOptions: Map<String, String> = emptyMap(),
    subjectOptions: Map<String, String> = emptyMap(),
    subjectOptionsByClassId: Map<String, Map<String, String>> = emptyMap(),
    teacherOptions: Map<String, String> = emptyMap(),
    teacherOptionsByClassSubject: Map<String, Map<String, String>> = emptyMap(),
    requiresTeacherSelection: Boolean = false,
    isSaving: Boolean = false,
    isLoading: Boolean = false,
    errorMessage: String = "",
    attachmentMetadata: Map<String, HomeworkAttachmentMetadataUi> = emptyMap(),
    onSave: (HomeworkDraft) -> Unit = {}
) {
    if (!visible) return

    key(
        initialHomework?.backendId ?: initialHomework?.homeworkId ?: "new",
        initialHomework?.title,
        initialHomework?.classSectionId,
        initialHomework?.subjectId,
        initialHomework?.dueDate,
        initialHomework?.description,
        initialHomework?.assignedByTeacherId,
        initialHomework?.attachmentFileIds?.joinToString(",")
    ) {
        var title by remember { mutableStateOf(initialHomework?.title.orEmpty()) }
        var selectedClass by remember { mutableStateOf(initialHomework?.className.orEmpty()) }
        var selectedSubject by remember { mutableStateOf(initialHomework?.subject.orEmpty()) }
        var selectedTeacher by remember { mutableStateOf(initialHomework?.assignedBy.orEmpty().takeUnless { it == "—" }.orEmpty()) }
        var assignedDate by remember { mutableStateOf(initialHomework?.assignedDate ?: currentAppDateLabel()) }
        var dueDate by remember { mutableStateOf(initialHomework?.dueDate ?: appDateLabelAfterToday()) }
        var description by remember { mutableStateOf(initialHomework?.description.orEmpty()) }
        var classExpanded by remember { mutableStateOf(false) }
        var subjectExpanded by remember { mutableStateOf(false) }
        var teacherExpanded by remember { mutableStateOf(false) }
        var isUploadingFiles by remember { mutableStateOf(false) }
        var attachmentError by remember { mutableStateOf("") }
        var hasSubmitted by remember { mutableStateOf(false) }
        val selectedClassId = classOptions[selectedClass].orEmpty()
        val selectedClassSubjectOptions = subjectOptionsByClassId[selectedClassId].orEmpty()
        val selectedSubjectId = selectedClassSubjectOptions[selectedSubject].orEmpty()
        val eligibleTeacherOptions = if (initialHomework == null) {
            teacherOptionsByClassSubject["$selectedClassId::$selectedSubjectId"].orEmpty()
        } else teacherOptions
        val titleError = if (hasSubmitted && title.isBlank()) "Homework title is required." else ""
        val classError = if (hasSubmitted && selectedClass.isBlank()) "Class is required." else ""
        val subjectError = if (hasSubmitted && selectedSubject.isBlank()) "Subject is required." else ""
        val teacherError = if (hasSubmitted && initialHomework == null && requiresTeacherSelection && selectedTeacher !in eligibleTeacherOptions) "Select a teacher assigned to this class and subject." else ""
        val dueDateError = if (hasSubmitted && dueDate.isBlank()) "Due date is required." else ""
        val descriptionError = if (hasSubmitted && description.isBlank()) "Homework description is required." else ""
        var uploadSession by remember(initialHomework?.backendId, initialHomework?.attachmentFileIds) {
            mutableStateOf(
                FileUploadRepository.newSession("HOMEWORK").copy(
                    recordId = initialHomework?.backendId?.takeIf { it.isNotBlank() },
                    uploadedFileIds = initialHomework?.attachmentFileIds.orEmpty()
                )
            )
        }
        val coroutineScope = rememberCoroutineScope()
        var attachments by remember(initialHomework?.backendId, attachmentMetadata) {
            mutableStateOf(
                if (initialHomework != null) initialHomework.attachmentFileIds.mapIndexed { index, fileId ->
                    val metadata = attachmentMetadata[fileId]
                    HomeworkAttachmentUi(
                        file = null,
                        name = metadata?.name ?: "Attachment ${index + 1}",
                        type = metadata?.type ?: "FILE",
                        sizeLabel = metadata?.sizeLabel ?: "Existing attachment",
                        fileId = fileId,
                        previewBytes = metadata?.previewBytes
                    )
                } else emptyList()
            )
        }
        val attachmentPicker = rememberFilePickerLauncher(
            type = PickerType.File(listOf("pdf", "docx", "jpg", "jpeg", "png")),
            mode = PickerMode.Multiple(),
            title = "Select Homework Attachments"
        ) { files ->
            if (files != null) coroutineScope.launch {
                isUploadingFiles = true
                attachmentError = ""
                for (file in files) {
                    if (attachments.any { it.name.equals(file.name, ignoreCase = true) }) continue
                    val bytes = runCatching { file.readBytes() }.getOrNull()
                    if (bytes == null || bytes.isEmpty()) {
                        attachmentError = "Unable to read ${file.name}."
                        continue
                    }
                    val isImage = file.name.substringAfterLast('.', "").lowercase() in setOf("jpg", "jpeg", "png", "webp")
                    val uploadBytes = if (isImage) resizeImage(bytes) else bytes
                    val uploadFilename = if (isImage && uploadBytes !== bytes) {
                        "${file.name.substringBeforeLast('.', file.name)}.jpg"
                    } else {
                        file.name
                    }
                    val result = FileUploadRepository.upload(
                        session = uploadSession,
                        filename = uploadFilename,
                        contentType = homeworkContentType(uploadFilename),
                        bytes = uploadBytes
                    )
                    result.fold(
                        onSuccess = { uploaded ->
                            uploadSession = uploaded.first
                            attachments = attachments + HomeworkAttachmentUi(
                                file = file,
                                name = file.name,
                                type = file.name.substringAfterLast('.', "FILE").uppercase(),
                                sizeLabel = formatHomeworkAttachmentSize(file.getSize()),
                                fileId = uploaded.second.fileId,
                                previewBytes = if (isImage) uploadBytes else null
                            )
                        },
                        onFailure = { throwable ->
                            attachmentError = throwable.message ?: "Unable to upload ${file.name}."
                        }
                    )
                }
                isUploadingFiles = false
            }
        }

    AppDialogScaffold(
        onDismiss = onDismiss,
        size = com.schoolconnect.app.designsystem.AppDialogSize.Wide,
        maxDialogHeight = 700.dp,
        isLoading = isLoading || isUploadingFiles,
        loadingTitle = if (isUploadingFiles) "Uploading attachments" else "Loading homework"
    ) {
        AppDialogHeader(
            title = if (initialHomework == null) "Add Homework" else "Edit Homework",
            titleSuffix = initialHomework?.homeworkId,
            subtitle = if (initialHomework == null) "Create a homework assignment." else "Update homework details.",
            onClose = onDismiss,
            colors = AppDialogDefaultColors
        )
        AppDialogValidationSummary(
            errors = listOf(titleError, classError, subjectError, teacherError, dueDateError, descriptionError),
            modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
        )
        AppDialogErrorMessage(
            message = errorMessage,
            modifier = Modifier.padding(horizontal = 28.dp, vertical = 4.dp)
        )
        HomeworkDialogBody {
            AppDialogFormGrid(modifier = Modifier.fillMaxWidth()) { compact ->
                if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(18.dp)) {
                        HomeworkTitleField(title, error = titleError) { title = it }
                        HomeworkClassField(selectedClass, classExpanded, classOptions.keys.sortedWith(compareBySmartText { it }), { classExpanded = true }, { classExpanded = false }, error = classError) { selectedClass = it; selectedSubject = ""; selectedTeacher = ""; classExpanded = false }
                        HomeworkSubjectField(selectedSubject, subjectExpanded, selectedClassSubjectOptions.keys.toList(), { subjectExpanded = true }, { subjectExpanded = false }, error = subjectError) { selectedSubject = it; selectedTeacher = ""; subjectExpanded = false }
                        HomeworkAssignedByField(selectedTeacher, teacherExpanded, eligibleTeacherOptions.keys.sortedWith(compareBySmartText { it }), initialHomework != null || !requiresTeacherSelection, { teacherExpanded = true }, { teacherExpanded = false }, error = teacherError) { selectedTeacher = it; teacherExpanded = false }
                        HomeworkDateFields(assignedDate, { assignedDate = it }, dueDate, { dueDate = it })
                    }
                } else {
                    Column(verticalArrangement = Arrangement.spacedBy(18.dp)) {
                        Row(horizontalArrangement = Arrangement.spacedBy(20.dp)) {
                            HomeworkTitleField(title, Modifier.weight(1f), titleError) { title = it }
                            HomeworkClassField(selectedClass, classExpanded, classOptions.keys.sortedWith(compareBySmartText { it }), { classExpanded = true }, { classExpanded = false }, Modifier.weight(1f), classError) { selectedClass = it; selectedSubject = ""; selectedTeacher = ""; classExpanded = false }
                        }
                        Row(horizontalArrangement = Arrangement.spacedBy(20.dp)) {
                            HomeworkSubjectField(selectedSubject, subjectExpanded, selectedClassSubjectOptions.keys.toList(), { subjectExpanded = true }, { subjectExpanded = false }, Modifier.weight(0.8f), subjectError) { selectedSubject = it; selectedTeacher = ""; subjectExpanded = false }
                            HomeworkAssignedByField(selectedTeacher, teacherExpanded, eligibleTeacherOptions.keys.sortedWith(compareBySmartText { it }), initialHomework != null || !requiresTeacherSelection, { teacherExpanded = true }, { teacherExpanded = false }, Modifier.weight(1.2f), teacherError) { selectedTeacher = it; teacherExpanded = false }
                        }
                        Row(horizontalArrangement = Arrangement.spacedBy(20.dp)) {
                            HomeworkDateField("Assigned Date", assignedDate, { assignedDate = it }, Modifier.weight(1f), "Automatically set to today's date.", readOnly = true)
                            HomeworkDateField("Due Date", dueDate, { dueDate = it }, Modifier.weight(1f), "Due date must be after assigned date.", "Select due date", error = dueDateError)
                        }
                    }
                }
            }

            AppDialogFieldColumn(label = "Homework Description", required = true, error = descriptionError, colors = AppDialogDefaultColors) {
                AppDialogTextField(
                    value = description,
                    onValueChange = { description = it },
                    placeholder = "Enter homework description... (Provide clear instructions for students.)",
                    colors = AppDialogDefaultColors,
                    singleLine = false,
                    fieldHeight = 96.dp,
                    fontSize = 14.sp,
                    error = descriptionError
                )
            }

            Spacer(Modifier.height(2.dp))
            AppDialogFormGrid(modifier = Modifier.fillMaxWidth()) { compact ->
                HomeworkCreatedByAndAttachment(compact, attachments, { attachmentPicker.launch() }, { removed ->
                    attachments = attachments - removed
                    removed.fileId
                        ?.takeIf { it !in initialHomework?.attachmentFileIds.orEmpty() }
                        ?.let { fileId -> coroutineScope.launch { FileUploadRepository.delete(fileId) } }
                }, includeCreatedBy = true)
            }

            if (attachmentError.isNotBlank()) {
                Text(attachmentError, color = AppDanger, fontSize = 12.sp, modifier = Modifier.padding(horizontal = 2.dp))
            }
        }
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction("Cancel", onDismiss, enabled = !isSaving && !isUploadingFiles && !isLoading),
                AppDialogFooterAction(
                    label = "Save",
                    onClick = {
                        hasSubmitted = true
                        if (listOf(titleError, classError, subjectError, teacherError, dueDateError, descriptionError).all { it.isBlank() }) {
                            coroutineScope.launch {
                                onSave(
                                    HomeworkDraft(
                                        homeworkId = initialHomework?.backendId?.takeIf { it.isNotBlank() },
                                        schoolId = initialHomework?.schoolId,
                                        title = title,
                                        classSectionId = classOptions[selectedClass].orEmpty().ifBlank { initialHomework?.classSectionId.orEmpty() },
                                        subjectId = selectedClassSubjectOptions[selectedSubject].orEmpty().ifBlank { initialHomework?.subjectId.orEmpty() },
                                        dueDate = dueDate,
                                        description = description,
                                        assignedByTeacherId = if (initialHomework == null) eligibleTeacherOptions[selectedTeacher] else null,
                                        attachmentFileIds = attachments.mapNotNull { it.fileId }
                                    )
                                )
                            }
                        }
                    },
                    style = AppDialogActionStyle.Primary,
                    enabled = !isSaving && !isUploadingFiles && !isLoading
                )
            ),
            colors = AppDialogDefaultColors,
            footerHeight = 64.dp
        )
        }
    }
}

@Composable
private fun HomeworkDialogBody(content: @Composable ColumnScope.() -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(max = 520.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = 28.dp, vertical = 8.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
            content = content
        )
    }
}

@Composable
private fun HomeworkCreatedByField(modifier: Modifier = Modifier) {
    val profile by ShellProfileStore.profile.collectAsState()
    AppDialogFieldColumn(
        label = "Created By",
        helperText = "Logged in user",
        colors = AppDialogDefaultColors,
        modifier = modifier
    ) {
        AppDialogTextField(
            value = profile.fullName.ifBlank { "Loading profile…" },
            onValueChange = {},
            placeholder = "Logged in user",
            colors = AppDialogDefaultColors,
            readOnly = true,
            leadingIcon = {
                Icon(
                    painter = painterResource(Res.drawable.user),
                    contentDescription = null,
                    tint = AppDialogDefaultColors.muted,
                    modifier = Modifier.size(20.dp)
                )
            }
        )
    }
}

@Composable
private fun HomeworkCreatedByAndAttachment(
    compact: Boolean,
    attachments: List<HomeworkAttachmentUi>,
    onUpload: () -> Unit,
    onRemove: (HomeworkAttachmentUi) -> Unit,
    includeCreatedBy: Boolean = true
) {

    @Composable
    fun UploadField(modifier: Modifier) {
        AppDialogFieldColumn(label = "Attachments", helperText = "Optional", colors = AppDialogDefaultColors, modifier = modifier) {
            Row(Modifier.fillMaxWidth().border(1.dp, com.schoolconnect.app.designsystem.AppBorder, RoundedCornerShape(8.dp)).padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                AppOutlinedButton(onClick = onUpload, modifier = Modifier.width(170.dp).height(44.dp)) {
                    Icon(painterResource(Res.drawable.cloud_upload), null, tint = AppPrimary, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Upload Files", color = AppPrimary, fontWeight = FontWeight.SemiBold)
                }
                Spacer(Modifier.width(14.dp))
                Text(if (attachments.isEmpty()) "No files selected" else "${attachments.size} file${if (attachments.size == 1) "" else "s"} selected", color = AppDialogDefaultColors.muted, fontSize = 13.sp)
            }
            Text("Supported: PDF, DOCX, JPG, PNG (Max 10 MB per file)", color = AppDialogDefaultColors.muted, fontSize = 12.sp)
        }
    }

    Column(verticalArrangement = Arrangement.spacedBy(18.dp)) {
        if (compact) {
            if (includeCreatedBy) HomeworkCreatedByField(Modifier.fillMaxWidth())
            UploadField(Modifier.fillMaxWidth())
        } else {
            if (includeCreatedBy) {
                Row(horizontalArrangement = Arrangement.spacedBy(20.dp)) {
                    HomeworkCreatedByField(Modifier.weight(1f))
                    UploadField(Modifier.weight(1f))
                }
            } else {
                UploadField(Modifier.fillMaxWidth())
            }
        }
        if (attachments.isNotEmpty()) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                attachments.chunked(2).forEach { row ->
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        row.forEach { attachment -> HomeworkAttachmentChip(attachment, Modifier.weight(1f), onRemove) }
                        if (row.size == 1) Spacer(Modifier.weight(1f))
                    }
                }
            }
        }
    }
}

@Composable
private fun HomeworkAttachmentChip(attachment: HomeworkAttachmentUi, modifier: Modifier, onRemove: (HomeworkAttachmentUi) -> Unit) {
    Row(modifier.height(72.dp).border(1.dp, com.schoolconnect.app.designsystem.AppBorder, RoundedCornerShape(8.dp)).padding(horizontal = 10.dp), verticalAlignment = Alignment.CenterVertically) {
        val preview = attachment.previewBytes?.let(::decodeImageBitmap)
        if (preview != null) {
            Image(
                bitmap = preview,
                contentDescription = attachment.name,
                modifier = Modifier.size(52.dp),
                contentScale = ContentScale.Fit
            )
        } else {
            Box(Modifier.size(36.dp).background(if (attachment.type == "PDF") Color(0xFFFFECEC) else Color(0xFFEAF8EF), RoundedCornerShape(6.dp)), contentAlignment = Alignment.Center) {
                Icon(painterResource(Res.drawable.document), null, tint = AppPrimary, modifier = Modifier.size(19.dp))
            }
        }
        Spacer(Modifier.width(8.dp))
        Column(Modifier.weight(1f)) {
            Text(attachment.name, color = AppDialogDefaultColors.navy, fontSize = 12.sp, maxLines = 1)
            Text("${attachment.type}  •  ${attachment.sizeLabel}", color = AppDialogDefaultColors.muted, fontSize = 11.sp)
        }
        Icon(painterResource(Res.drawable.close_x), "Remove ${attachment.name}", tint = AppDialogDefaultColors.navy, modifier = Modifier.size(18.dp).clickable { onRemove(attachment) })
    }
}

private fun formatHomeworkAttachmentSize(bytes: Long?): String {
    if (bytes == null || bytes <= 0L) return "Size unavailable"
    return if (bytes >= 1024L * 1024L) "${bytes / (1024L * 1024L)} MB" else "${(bytes / 1024L).coerceAtLeast(1L)} KB"
}

private fun homeworkContentType(filename: String): String = when (filename.substringAfterLast('.', "").lowercase()) {
    "pdf" -> "application/pdf"
    "docx" -> "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    "jpg", "jpeg" -> "image/jpeg"
    "png" -> "image/png"
    else -> "application/octet-stream"
}

@Composable
private fun HomeworkTitleField(value: String, modifier: Modifier = Modifier, error: String = "", onValueChange: (String) -> Unit) {
    AppDialogFieldColumn(label = "Homework Title", required = true, colors = AppDialogDefaultColors, modifier = modifier) {
        AppDialogTextField(value, onValueChange, "Enter homework title", colors = AppDialogDefaultColors, maxLength = 200, error = error)
    }
}

@Composable
private fun HomeworkClassField(value: String, expanded: Boolean, options: List<String>, onExpand: () -> Unit, onDismiss: () -> Unit, modifier: Modifier = Modifier, error: String = "", onSelected: (String) -> Unit) {
    AppDialogFieldColumn(label = "Class", required = true, colors = AppDialogDefaultColors, modifier = modifier) {
        AppDialogSelectField(value, "Search class", expanded, options, onExpand, onDismiss, onSelected, colors = AppDialogDefaultColors, error = error)
    }
}

@Composable
private fun HomeworkSubjectField(value: String, expanded: Boolean, options: List<String>, onExpand: () -> Unit, onDismiss: () -> Unit, modifier: Modifier = Modifier, error: String = "", onSelected: (String) -> Unit) {
    AppDialogFieldColumn(label = "Subject", required = true, colors = AppDialogDefaultColors, modifier = modifier) {
        AppDialogSelectField(value, "Search subject", expanded, options, onExpand, onDismiss, onSelected, colors = AppDialogDefaultColors, error = error)
    }
}

@Composable
private fun HomeworkAssignedByField(value: String, expanded: Boolean, options: List<String>, readOnly: Boolean, onExpand: () -> Unit, onDismiss: () -> Unit, modifier: Modifier = Modifier, error: String = "", onSelected: (String) -> Unit) {
    val profile by ShellProfileStore.profile.collectAsState()
    AppDialogFieldColumn(label = "Assigned By (Teacher)", required = !readOnly, colors = AppDialogDefaultColors, modifier = modifier) {
        if (readOnly) {
            AppDialogTextField(value = value.ifBlank { profile.fullName }.ifBlank { "Assigned teacher" }, onValueChange = {}, placeholder = "Assigned teacher", colors = AppDialogDefaultColors, readOnly = true)
        } else {
            AppDialogSelectField(value, "Search teacher", expanded, options, onExpand, onDismiss, onSelected, colors = AppDialogDefaultColors, error = error, emptyLabel = "No teachers available")
        }
    }
}

@Composable
private fun HomeworkDateFields(assigned: String, onAssigned: (String) -> Unit, due: String, onDue: (String) -> Unit) {
    Row(horizontalArrangement = Arrangement.spacedBy(20.dp)) {
        HomeworkDateField("Assigned Date", assigned, onAssigned, Modifier.weight(1f), "Automatically set to today's date.", readOnly = true)
        HomeworkDateField("Due Date", due, onDue, Modifier.weight(1f), "Due date must be after assigned date.", "Select due date")
    }
}

@Composable
private fun HomeworkDateField(label: String, value: String, onValueChange: (String) -> Unit, modifier: Modifier, helper: String, placeholder: String = "DD MMM YYYY", readOnly: Boolean = false, error: String = "") {
    AppDialogFieldColumn(label = label, required = true, helperText = helper, colors = AppDialogDefaultColors, modifier = modifier, error = error) {
        AppDialogDateField(value, onValueChange, placeholder = placeholder, colors = AppDialogDefaultColors, fontSize = 14.sp, readOnly = readOnly, error = error)
    }
}
